import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import ChatWindow from './ChatWindow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [oficialInfo, setOficialInfo] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOficialInfo();
    fetchUnreadCount();
    
    // Poll for unread count
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchOficialInfo = async () => {
    try {
      setLoading(true);
      // Get the client's case to find their assigned oficial
      const caseRes = await axios.get(`${API}/cases/my-case`);
      if (caseRes.data?.oficial_id) {
        // Get oficial details
        const oficialId = caseRes.data.oficial_id;
        const oficialRes = await axios.get(`${API}/users/${oficialId}`);
        setOficialInfo({
          id: oficialId,
          name: oficialRes.data.name || 'Tu Oficial'
        });
      }
    } catch (error) {
      console.error('Error fetching oficial info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/chat/unread-count`);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0); // Reset count when opening
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Don't show if no oficial assigned or still loading
  if (loading || !oficialInfo) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleOpen}
          className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center z-40 hover:shadow-xl transition-shadow"
          data-testid="chat-floating-btn"
        >
          <MessageCircle className="w-6 h-6" />
          
          {/* Unread Badge */}
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </motion.button>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            oficialId={oficialInfo.id}
            oficialName={oficialInfo.name}
            onClose={handleClose}
            isMinimized={isMinimized}
            onToggleMinimize={handleToggleMinimize}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatButton;
